import {
  TrendingUp,
  TrendingDown,
  Pencil,
  Trash2,
  ArrowLeftRight,
  ArrowRight,
  Repeat,
} from 'lucide-react';
import { useMemo } from 'react';
import { useCurrency } from '@/contexts';
import { useExchangeRates } from '@/hooks/useExchangeRates';
import { useTranslation } from '@/hooks/useTranslation';
import { CATEGORY_ICONS } from '@/hooks/useCategories';
import type { Database } from '@/lib/database.types';

type Transaction = Database['public']['Tables']['transactions']['Row'] & {
  category?: Database['public']['Tables']['categories']['Row'] | null;
  account?: Database['public']['Tables']['accounts']['Row'] | null;
  to_account?: Database['public']['Tables']['accounts']['Row'] | null;
};

interface TransactionItemProps {
  transaction: Transaction;
  onEdit: (transaction: Transaction) => void;
  onDelete: (id: string) => void;
  showAccount?: boolean;
  highlightAccountId?: string;
}

export function TransactionItem({
  transaction,
  onEdit,
  onDelete,
  showAccount = true,
  highlightAccountId,
}: TransactionItemProps) {
  const { formatAmountInCurrency } = useCurrency();
  const { t } = useTranslation();

  const accountCurrency = transaction.account?.currency || 'EUR';

  const getCategoryIcon = (iconName: string | null) => {
    if (!iconName) return CATEGORY_ICONS['default'];
    if (CATEGORY_ICONS[iconName]) return CATEGORY_ICONS[iconName];
    // eslint-disable-next-line no-control-regex
    if (/[^\u0000-\u007F]/.test(iconName)) return iconName;
    return CATEGORY_ICONS['default'];
  };

  const isTransfer = transaction.type === 'transfer';
  const isIncome = transaction.type === 'income';

  const isTransferIn =
    isTransfer && highlightAccountId && transaction.to_account_id === highlightAccountId;
  const isTransferOut =
    isTransfer && highlightAccountId && transaction.account_id === highlightAccountId;

  const getAmountClass = () => {
    if (isTransfer) {
      if (isTransferIn) return 'text-emerald-600 dark:text-emerald-400';
      if (isTransferOut) return 'text-red-500 dark:text-red-400';
      return 'text-blue-600 dark:text-blue-400';
    }
    if (isIncome) return 'text-emerald-600 dark:text-emerald-400';
    return 'text-[#78909C]';
  };

  const getAmountPrefix = () => {
    if (isTransfer) {
      if (isTransferIn) return '+';
      if (isTransferOut) return '\u2212';
      return '';
    }
    if (isIncome) return '+';
    return '\u2212';
  };

  const iconConfig = isTransfer
    ? {
        icon: ArrowLeftRight,
        bg: 'bg-blue-500/10 dark:bg-blue-500/20',
        text: 'text-blue-500 dark:text-blue-400',
      }
    : isIncome
      ? {
          icon: TrendingUp,
          bg: 'bg-emerald-500/10 dark:bg-emerald-500/20',
          text: 'text-emerald-500 dark:text-emerald-400',
        }
      : {
          icon: TrendingDown,
          bg: 'bg-[#78909C]/10 dark:bg-[#78909C]/20',
          text: 'text-[#78909C]',
        };
  const Icon = iconConfig.icon;

  return (
    <div className="group flex items-center gap-4 px-5 py-3 hover:bg-gray-50/60 dark:hover:bg-white/[0.02] transition-colors duration-150 cursor-default">
      {/* Icon */}
      <div
        className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconConfig.bg}`}
      >
        <Icon className={`w-4 h-4 ${iconConfig.text}`} strokeWidth={2} />
      </div>

      {/* Description */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-[13px] font-semibold truncate leading-tight ${
            transaction.is_pending
              ? 'text-gray-400 dark:text-gray-500 italic'
              : 'text-gray-900 dark:text-white'
          }`}
        >
          {transaction.description ||
            (isTransfer ? t('transactions.transfer') : t('transactions.noDescription'))}
        </p>
        {/* Authorised but not settled: shown so the spend is visible the same
            day, marked so it is not mistaken for a confirmed amount, and left
            out of every total. */}
        {transaction.is_pending && (
          <span className="mt-0.5 inline-block rounded-full bg-amber-100 px-1.5 py-px text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
            {t('transactions.pending')}
          </span>
        )}
        {transaction.is_recurring && (
          <span className="text-[10px] text-amber-500 dark:text-amber-400 font-medium flex items-center gap-0.5 mt-0.5">
            <Repeat className="w-2.5 h-2.5" />
          </span>
        )}
      </div>

      {/* Category */}
      <div className="hidden md:block w-[140px] flex-shrink-0">
        {!isTransfer && transaction.category ? (
          <span className="text-[12px] text-gray-500 dark:text-gray-400 truncate flex items-center gap-1.5">
            <span className="text-sm leading-none">
              {getCategoryIcon(transaction.category.icon)}
            </span>
            <span className="truncate">{transaction.category.name}</span>
          </span>
        ) : isTransfer && transaction.account && transaction.to_account ? (
          <span className="text-[12px] text-gray-500 dark:text-gray-400 truncate flex items-center gap-1">
            {transaction.account.name}
            <ArrowRight className="w-3 h-3 opacity-40 flex-shrink-0" />
            {transaction.to_account.name}
          </span>
        ) : (
          <span className="text-[12px] text-gray-300 dark:text-gray-600">&mdash;</span>
        )}
      </div>

      {/* Account */}
      {showAccount && (
        <div className="hidden lg:block w-[120px] flex-shrink-0">
          <span className="text-[12px] text-gray-400 dark:text-gray-500 truncate block">
            {transaction.account?.name || '\u2014'}
          </span>
        </div>
      )}

      {/* Amount */}
      <div className="text-right w-[110px] flex-shrink-0">
        <p className={`text-[13px] font-bold tabular-nums tracking-tight ${getAmountClass()}`}>
          {getAmountPrefix()}
          {formatAmountInCurrency(transaction.amount, accountCurrency)}
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex-shrink-0">
        <button
          onClick={() => onEdit(transaction)}
          className="p-1.5 rounded-lg hover:bg-gray-200/70 dark:hover:bg-gray-600/50 transition-colors"
          aria-label="Edit"
        >
          <Pencil className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400" />
        </button>
        <button
          onClick={() => onDelete(transaction.id)}
          className="p-1.5 rounded-lg hover:bg-red-100/70 dark:hover:bg-red-900/30 transition-colors"
          aria-label="Delete"
        >
          <Trash2 className="w-3.5 h-3.5 text-gray-400 group-hover:text-red-400 dark:text-gray-500 dark:group-hover:text-red-400" />
        </button>
      </div>
    </div>
  );
}

// ─── Grouped Transaction List ────────────────────────────────────────

interface GroupedTransactionListProps {
  transactions: Transaction[];
  onEdit: (transaction: Transaction) => void;
  onDelete: (id: string) => void;
  showAccount?: boolean;
  highlightAccountId?: string;
  language?: string;
}

export function GroupedTransactionList({
  transactions,
  onEdit,
  onDelete,
  showAccount = true,
  highlightAccountId,
  language = 'en',
}: GroupedTransactionListProps) {
  const { t } = useTranslation();
  const { formatAmount, currency } = useCurrency();
  const { getRate } = useExchangeRates();

  const grouped = useMemo(() => {
    const groups: Record<string, Transaction[]> = {};
    for (const tx of transactions) {
      const dateKey = tx.date.split('T')[0];
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(tx);
    }
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([dateKey, txs]) => ({ dateKey, transactions: txs }));
  }, [transactions]);

  const formatDateLabel = (dateKey: string) => {
    const date = new Date(dateKey + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const dateNoTime = new Date(date);
    dateNoTime.setHours(0, 0, 0, 0);

    if (dateNoTime.getTime() === today.getTime()) return t('transactions.today' as any);
    if (dateNoTime.getTime() === yesterday.getTime()) return t('transactions.yesterday' as any);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  };

  const getDaySummary = (txs: Transaction[]) => {
    let income = 0;
    let expense = 0;
    for (const tx of txs) {
      // Pending operations are shown but never counted: the amount can still
      // change and the bank can drop it entirely.
      if (tx.is_pending) continue;
      const acctCurrency = (tx as any).account?.currency || 'EUR';
      const rate = getRate(acctCurrency, currency) ?? 1;
      if (tx.type === 'income') income += tx.amount * rate;
      else if (tx.type === 'expense') expense += tx.amount * rate;
    }
    return { income, expense };
  };

  return (
    <div className="space-y-5">
      {grouped.map(({ dateKey, transactions: txs }) => {
        const summary = getDaySummary(txs);
        return (
          <div key={dateKey}>
            {/* Date header */}
            <div className="flex items-center justify-between px-5 mb-1">
              <span className="text-[12px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                {formatDateLabel(dateKey)}
              </span>
              <div className="flex items-center gap-2 flex-shrink-0">
                {summary.income > 0 && (
                  <span className="text-[11px] font-medium text-emerald-500 dark:text-emerald-400 tabular-nums">
                    +{formatAmount(summary.income)}
                  </span>
                )}
                {summary.expense > 0 && (
                  <span className="text-[11px] font-medium text-[#78909C] tabular-nums">
                    &minus;{formatAmount(summary.expense)}
                  </span>
                )}
              </div>
            </div>

            {/* Transactions */}
            <div className="bg-white dark:bg-gray-800/50 rounded-xl overflow-hidden">
              {txs.map((transaction) => (
                <TransactionItem
                  key={transaction.id}
                  transaction={transaction}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  showAccount={showAccount}
                  highlightAccountId={highlightAccountId}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
